<script setup lang="ts">
// validation
import { defineRule, useForm, useField } from 'vee-validate';
import { email, required } from '@vee-validate/rules';

// validation rules
defineRule('email', email);
defineRule('required', required);

definePageMeta({
  layout: 'simple',
});

/********************
 * COMPOSITIONS      *
 ********************/
const localePath = useLocalePath();
const userService = useUserService();
const { $gettext, $pgettext } = useGettext();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const error = ref(null);
const loading = ref(false);
const success = ref(null);

// form
const { validate } = useForm({
  validationSchema: {
    email: 'required|email',
  },
});

const { value: emailValue, errorMessage: emailError } = useField<string>('email');

const i18nGetEmailLabel = computed(() => {
  return $gettext('E-Mail');
});

const i18nGetResetButtonLabel = computed(() => {
  const text = loading.value ? $gettext('Please wait…') : $pgettext('Account form button', 'Reset password');
  return text;
});

const finished = computed(() => {
  return success.value !== null;
});

/********************
 * FUNCTIONS         *
 ********************/

function onFinish() {
  return navigateTo(localePath({ name: 'index' }));
}

async function submit() {
  const { valid } = await validate();
  if (!valid) {
    return false;
  }

  error.value = null;
  loading.value = true;
  success.value = null;

  userService
    .resetPassword(locale.value, { email: emailValue.value })
    .then((res) => {
      if (res !== null) {
        loading.value = false;
        success.value = $gettext('Password reset e-mail has been sent.');
      }
    })
    .catch((err) => {
      if (err != null) {
        loading.value = false;
        if (err?.response?._data !== null) {
          error.value = err.response._data;
        }
      }
    });

  return true;
}
</script>

<template>
  <client-only>
    <section class="mx-auto w-full max-w-[480px] px-4 py-0">
      <transition
        name="slide-fade"
        appear
      >
        <div class="reset-password-form">
          <h1 class="mb-6 text-xl">
            {{ $pgettext('Account form title', 'Reset Password') }}
          </h1>
          <form @submit.prevent="submit">
            <InputTextfield
              id="email"
              v-model="emailValue"
              :label="i18nGetEmailLabel"
              :error="!!emailError"
              :class="{
                hidden: finished,
              }"
              type="email"
              inverted
              name="email"
            />

            <CiInfoPanel
              :text="success || undefined"
              :condition="success !== null"
              type="success"
            />

            <CiInfoPanel
              :condition="error !== null"
              type="danger"
            >
              <CiDjangoErrorMessages :errors="error" />
            </CiInfoPanel>

            <CiButton
              :disabled="loading"
              :class="[loading ? 'button--disabled' : 'button--primary', { hidden: finished }]"
              class="button icon-absolute w-full"
              type="submit"
            >
              <span>{{ i18nGetResetButtonLabel }}</span>
            </CiButton>

            <CiButton
              :class="{
                hidden: !finished,
              }"
              class="button button--primary icon-absolute w-full"
              @click="onFinish"
            >
              <span>{{ $gettext('Close') }}</span>
            </CiButton>
          </form>
        </div>
      </transition>
    </section>
  </client-only>
</template>
